/**
 * Blog readability
 *
 * Articles here average 1,800 words across 7.6 H2 sections with no way to
 * navigate them, an FAQ block rendered as flat text, and long runs of labelled
 * list items that read as a wall. This adds the structure the content already
 * implies, without editing a single article body:
 *
 *   1. "In this guide" contents, built from the H2s
 *   2. FAQ questions become native <details> accordions
 *   3. <li><strong>Label:</strong> ...</li> becomes a scannable labelled row
 *   4. Tables get a scroll container so they never break the page
 *   5. Tags move below the article so the title leads
 */
(function () {
  'use strict';

  function slugify(text) {
    return text.toLowerCase().trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60);
  }

  /* ---------- 1. Contents ---------- */
  function buildContents(root) {
    var heads = Array.prototype.filter.call(root.children, function (n) {
      return n.tagName === 'H2' && n.textContent.trim();
    });
    if (heads.length < 4) return; // short pieces do not need navigation

    var used = {};
    var nav = document.createElement('nav');
    nav.className = 'blog-toc';
    nav.setAttribute('aria-labelledby', 'blog-toc-title');

    var heading = document.createElement('h2');
    heading.className = 'blog-toc__title';
    heading.id = 'blog-toc-title';
    heading.textContent = 'In this guide';
    nav.appendChild(heading);

    var ol = document.createElement('ol');
    ol.className = 'blog-toc__list';

    heads.forEach(function (h) {
      var base = slugify(h.textContent) || 'section';
      var id = base;
      var n = 2;
      while (used[id]) { id = base + '-' + n; n++; }
      used[id] = true;
      if (!h.id) h.id = id;

      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent.trim();
      li.appendChild(a);
      ol.appendChild(li);
    });

    nav.appendChild(ol);

    // Place it after the opening paragraphs, before the first section starts.
    heads[0].parentNode.insertBefore(nav, heads[0]);
  }

  /* ---------- 2. FAQ accordions ---------- */
  function buildFaq(root) {
    var faqHead = Array.prototype.find.call(root.children, function (n) {
      return n.tagName === 'H2' && /frequently asked|^faq\b|common questions/i.test(n.textContent);
    });
    if (!faqHead) return;

    var wrap = document.createElement('div');
    wrap.className = 'blog-faq';

    var node = faqHead.nextElementSibling;
    var count = 0;

    while (node && node.tagName !== 'H2') {
      var next = node.nextElementSibling;

      if (node.tagName === 'H3') {
        var details = document.createElement('details');
        details.className = 'blog-faq__item';

        var summary = document.createElement('summary');
        summary.className = 'blog-faq__q';
        summary.textContent = node.textContent.trim();
        details.appendChild(summary);

        var answer = document.createElement('div');
        answer.className = 'blog-faq__a';

        // Everything until the next question belongs to this answer.
        var body = next;
        while (body && body.tagName !== 'H3' && body.tagName !== 'H2') {
          var after = body.nextElementSibling;
          answer.appendChild(body);
          body = after;
        }
        details.appendChild(answer);
        wrap.appendChild(details);

        node.parentNode.removeChild(node);
        node = body;
        count++;
        continue;
      }
      node = next;
    }

    if (!count) return;
    faqHead.parentNode.insertBefore(wrap, faqHead.nextSibling);
    faqHead.classList.add('blog-faq__heading');
  }

  /* ---------- 3. Labelled list rows ---------- */
  function markLabelledLists(root) {
    Array.prototype.forEach.call(root.querySelectorAll('li'), function (li) {
      var first = li.firstElementChild;
      if (!first || first.tagName !== 'STRONG') return;
      var label = first.textContent.trim();
      if (!label) return;
      // Only treat it as a label when the rest of the item carries the detail.
      if (li.textContent.trim().length <= label.length + 10) return;
      li.classList.add('blog-li--labelled');
      first.classList.add('blog-li__label');
      if (li.parentElement) li.parentElement.classList.add('blog-list--labelled');
    });
  }

  /* ---------- 4. Tables ---------- */
  function wrapTables(root) {
    Array.prototype.forEach.call(root.querySelectorAll('table'), function (t) {
      if (t.parentElement && t.parentElement.classList.contains('blog-table')) return;
      var wrap = document.createElement('div');
      wrap.className = 'blog-table';
      t.parentNode.insertBefore(wrap, t);
      wrap.appendChild(t);
    });
  }

  /* ---------- 5. Tags below the article ---------- */
  function demoteTags(root) {
    var tags = document.querySelector('.article-tags, .article-header__tags, [class*="article"] [class*="tags"]');
    if (!tags || tags.closest('.article-content')) return;
    if (tags.getAttribute('data-demoted')) return;
    tags.setAttribute('data-demoted', '');
    tags.classList.add('blog-tags--footer');
    root.parentNode.insertBefore(tags, root.nextSibling);
  }

  /* ---------- 6. Reading progress ---------- */
  function readingProgress(root) {
    var bar = document.createElement('div');
    bar.className = 'blog-progress';
    bar.innerHTML = '<span class="blog-progress__fill"></span>';
    document.body.appendChild(bar);
    var fill = bar.firstChild;
    var ticking = false;

    function update() {
      var rect = root.getBoundingClientRect();
      var total = rect.height - window.innerHeight;
      var done = Math.min(Math.max(-rect.top, 0), Math.max(total, 1));
      fill.style.width = (total > 0 ? (done / total) * 100 : 0) + '%';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  function init() {
    var root = document.querySelector('.article-content');
    if (!root || root.getAttribute('data-readability')) return;
    root.setAttribute('data-readability', '');

    // Order matters: pull the FAQ out before the contents is built, so the
    // contents links point at headings that are still in place.
    buildContents(root);
    buildFaq(root);
    markLabelledLists(root);
    wrapTables(root);
    demoteTags(root);
    readingProgress(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
